import React, { memo, useCallback, useState } from 'react';
import {
  getDeletesForSegment,
  getEditsForSegment,
  getScenesForSegment
} from '../../../ClipNewTimeline/playerConfigUtil';
import { getEndForSegment, getStartForSegment } from '@/player-config/src/utils/clipUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';
import IconButton from '@/components/atoms/Button/IconButton';
import TooltipV2 from '@/components/TooltipV2';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';

interface CopyClipButtonProps {
  className?: string;
  onCopySuccess?: (newClipId: string) => void;
}

const CopyClipButton: React.FC<CopyClipButtonProps> = memo(({ className = '', onCopySuccess }) => {
  const { clipData } = useClipsContext();
  const { createClip } = useTranscriptPage();
  const [isLoading, setIsLoading] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopyClip = useCallback(async () => {
    if (!clipData?.id || isLoading) {
      showErrorToast('No clip selected to copy');
      return;
    }

    setIsLoading(true);

    try {
      const { asset_metadata } = clipData;

      await createClip(
        getStartForSegment({
          clip: clipData,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        getEndForSegment({
          clip: clipData,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        {
          edits: getEditsForSegment(clipData) || {},
          deletes: getDeletesForSegment(clipData) || {},
          scenes: getScenesForSegment(clipData),
          additionalParams: { is_copied_for_ai_edits_testing: true, player_config: asset_metadata.player_config }
        }
      );

      // Show success feedback
      showSuccessToast('Clip copied successfully for AI edits testing');

      // Visual feedback
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);

      // Callback for parent component (note: createClip doesn't return clip ID)
      onCopySuccess?.('');
    } catch (error) {
      window?.logger?.error('Failed to copy clip:', error);
      showErrorToast('Failed to copy clip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [clipData, createClip, isLoading, onCopySuccess]);

  const getTooltipContent = () => {
    if (isLoading) {
      return 'Copying clip...';
    }
    if (hasCopied) {
      return 'Clip copied successfully!';
    }
    return 'Copy clip with current configuration';
  };

  return (
    <div
      className={`flex h-8 flex-none rounded-lg border transition-all duration-500 hover:border-blue-50 hover:bg-blue-50 hover:text-blue-600 ${className}`}
    >
      <TooltipV2
        tooltipElement={
          <IconButton
            icon={hasCopied ? 'IconCheck' : 'IconCopy'}
            variation="outline"
            size="base"
            onClick={handleCopyClip}
            disabled={isLoading || !clipData?.id}
            buttonClassName={`size-8 ${hasCopied ? 'text-green-600' : ''} ${isLoading ? 'animate-pulse' : ''}`}
            trackingId="copy-clip-button"
          />
        }
        tooltipId="copy-clip-tooltip"
        tooltipContent={getTooltipContent()}
        colorTheme="dark"
        size="xsmall"
        place="top"
      />
    </div>
  );
});

export default CopyClipButton;
